import { computed, inject, Injectable, signal } from '@angular/core'
import { User, USER_TYPES } from '../interfaces/users.interface'
import { UsersService } from '../services/users.service'

@Injectable({
  providedIn: 'root'
})
export class UsersStore {
  #usersService = inject(UsersService)

  #user$ = signal<User | null>(null)
  user = this.#user$.asReadonly()

  isAuthenticated = computed(() => this.user()?.authenticated)

  #users$ = signal<User[]>([])
  users = this.#users$.asReadonly()

  #agencias$ = signal<User[]>([])
  agencias = this.#agencias$.asReadonly()

  #escuderias$ = signal<User[]>([])
  escuderias = this.#escuderias$.asReadonly()

  getUsersByType(type: USER_TYPES) {
    const users = this.users()
    return users.filter(user => user.userType === type)
  }

  getAgenciaUsers(agencia: User) {
    const users = this.users()
    return users.filter(user => user.agencia === agencia.agencia)
  }

  getEscuderiaUsers(escuderia: User) {
    const users = this.users()
    return users.filter(user => user.escuderia === escuderia.escuderia)
  }

  async loadCurrentUser() {
    const user = await this.#usersService.getCurrentUser()
    this.#user$.set(user)

    return user
  }

  async loadEscuderias() {
    const users = await this.#usersService.getEscuderias()
    this.#escuderias$.set(users)

    return users
  }

  async loadAgencias() {
    const users = await this.#usersService.getAgencias()
    this.#agencias$.set(users)

    return users
  }

  async loadEscuderiaUsers(user: User) {
    const { escuderia, totalUsers } = user

    if (!escuderia || !totalUsers) return []

    const users = await this.#usersService.getEscuderiaUsers(escuderia, totalUsers)
    this.#users$.set(users)

    return users
  }

  async loadAgenciaUsers(user: User) {
    const { agencia, escuderia, totalUsers } = user

    if (!agencia || !escuderia || !totalUsers) return []

    const users = await this.#usersService.getAgenciaUsers(agencia, escuderia, totalUsers)
    this.#users$.set(users)

    return users
  }

  async loadAllUsers(escuderias: User[]) {
    const users = await this.#usersService.getAllUsers(escuderias)
    this.#users$.set(users)

    return users
  }
}
